import { inject, Injectable } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import type { Data } from '@angular/router'
import { NavigationEnd, Router } from '@angular/router'
import { distinctUntilChanged, filter, map } from 'rxjs/operators'

import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from './meta.model'

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private readonly router = inject(Router)
  private readonly title = inject(Title)
  private readonly meta = inject(Meta)

  private readonly pageDataChanged$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => {
      let data: Data = {}
      let route = this.router.routerState.root
      while (route.firstChild) {
        route = route.firstChild
        data = { ...data, ...route.snapshot.data }
      }
      return data
    }),
    distinctUntilChanged(),
  )

  init(): void {
    this.pageDataChanged$.subscribe(data => {
      this.setCanonical(`${window.location.origin}${window.location.pathname}`)
      this.setDescription(data.description)
      this.setTitle(data.title)
      this.setRobots(data.robots)
    })
  }

  private setDescription(description: string | undefined): void {
    const content = description || DEFAULT_DESCRIPTION

    this.meta.updateTag({
      name: 'description',
      content,
    })

    this.meta.updateTag({
      name: 'twitter:description',
      content,
    })
  }

  private setTitle(title: string | undefined): void {
    const content = title || DEFAULT_TITLE
    this.title.setTitle(content)

    this.meta.updateTag({
      property: 'og:title',
      name: 'twitter:title',
      content,
    })
  }

  private setRobots(content: string | undefined): void {
    if (content) {
      this.meta.updateTag({
        name: 'robots',
        content,
      })
    } else {
      this.meta.removeTag("name='robots'")
    }
  }

  private setCanonical(url: string | undefined): void {
    let canonicalLinkElement = document.querySelector('link[rel="canonical"]')

    if (url) {
      if (!canonicalLinkElement) {
        canonicalLinkElement = document.createElement('link')
        canonicalLinkElement.setAttribute('rel', 'canonical')
        document.head.append(canonicalLinkElement)
      }
      canonicalLinkElement.setAttribute('href', url)
    } else if (canonicalLinkElement) {
      canonicalLinkElement.remove()
    }
  }
}
